import React, { useCallback, useMemo, useState } from "react";
import VStack from "../../ui_atoms/VStack";
import { Cct, CctDetailsFieldValueInput, Mission } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import {
    GET_CCT_DETAILS_FIELD_VALUES_FROM_PARENT,
    GET_CCT_DETAILS_FIELDS,
} from "../../graphql/cct";
import SelectField from "../SelectField";
import {
    CctDetailsFieldFieldsFragment,
    CctDetailsFieldFullValueFieldsFragment,
    CctDetailsFieldValuesFieldsFragment,
} from "../../__generated__/operations/graphql";
import { getAge, hasLength, range } from "../../utils";
import { Buildable } from "ts-essentials";
import { SelectItemType } from "../select/SelectChooser";
import { BodyLargeText, BodyMediumText, LinkSmallText } from "../FormattedText";
import Loading from "../Loading";
import ScreenCardHeadband from "../../screens/ScreenCardHeadband";
import FullWidthLine from "../FullWidthLine";
import { useTheme } from "../../hooks/use-theme";
import { useIntercom } from "../../hooks/intercom";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { useCatchLazyQueryError } from "../../hooks/errors";
import QuantityField from "../QuantityField";
import { differenceInMonths } from "date-fns";

const AUTO_FILLED_FIELDS = {
    YEAR: ["jahr", "année", "year"],
    AGE: ["alter", "âge", "age"],
    MISSION_DURATION: ["mission_duration_month"],
};

// The CCT queries only ever select the fragment fields; `path` and `fullValues` are never
// fetched, and the values are sent back as CCTDetailsFieldValueInput (which has no `path`).
// Typing local state by the operation fragments (instead of the full schema types) keeps the
// runtime objects free of unsent fields and avoids casts. `children` is spread manually in the
// query and is absent at the deepest level, so it is optional here.
type CctField = CctDetailsFieldFieldsFragment & { children?: CctField[] | null };

type MissionCctDetailsProps = {
    mission: Buildable<Mission>;
    missionCct: Cct;
    setCctDetails: (cctDetails: CctDetailsFieldFullValueFieldsFragment) => void;
    setScreenIsValid: (value: boolean) => void;
};

const MissionCctDetails: React.FC<MissionCctDetailsProps> = ({
    mission,
    missionCct,
    setCctDetails,
    setScreenIsValid,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { presentArticleId } = useIntercom();
    const {
        data: allFields,
        error: getFieldsError,
        loading: getFieldLoading,
    } = useQuery(GET_CCT_DETAILS_FIELDS, {
        fetchPolicy: "no-cache",
        variables: {
            cctEditionId: mission.currentContract?.cctEditionId || "",
        },
    });

    const missionStateCode = useMemo(
        () => mission.currentContract?.workingAddress?.locality?.state?.code || "",
        [mission],
    );
    const missionStateName = useMemo(
        () => mission.currentContract?.workingAddress?.locality?.state?.name || "",
        [mission],
    );

    const [getCctDetailsFieldValues, { error }] = useLazyQuery(
        GET_CCT_DETAILS_FIELD_VALUES_FROM_PARENT,
    );

    useApolloErrorSnackbar(error);
    const catchLazyQueryError = useCatchLazyQueryError();

    const [loadingError, setLoadingError] = useState<string>();

    // this array contains all fields that are flattened (all child of field are placed just after their parent).
    const [flattenedFields, setFlattenedFields] = useState<CctField[]>([]);
    // this array has the same size of flattenedFields and contains array of possible
    // values for the field at same index. As children values depend on parent value, they will be fetched
    // when parent value is set.
    // we use a ref as we just need this array to be mutable and we do not want to trigger a rerender when it changes.
    const flattenedValuesRef = React.useRef<CctDetailsFieldValuesFieldsFragment[][]>([]);

    React.useEffect(() => {
        const asyncMethod = async () => {
            // This effect is used when we load the screen and already have cct details. We need to check if we are full to set the complete flag
            if (!getFieldLoading && flattenedFields && flattenedFields.length > 0) {
                //when we first receive the flattened fields we check if cct details is complete or not.
                const missionCctFields = mission.currentContract
                    ?.cctDetails as CctDetailsFieldFullValueFieldsFragment;
                // we need to fetch children field of already seléected field
                for (let index = 0; index < flattenedFields.length; index++) {
                    if (missionCctFields.tempdataCallValues.length > index) {
                        await fetchChildrenValues(
                            flattenedFields[index],
                            mission.currentContract?.cctEditionId || "",
                            missionCctFields.tempdataCallValues.slice(0, index + 1),
                            missionCctFields.tempdataCallValues[index]?.value || null,
                        );
                    }
                }
                setScreenIsValid(
                    missionCctFields.tempdataCallValues.length === flattenedFields.length,
                );
                setCctDetails(missionCctFields);
            }
        };
        void asyncMethod();
    }, [flattenedFields, setCctDetails, getFieldLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        // This effect should run only once when the fields for the CCT are fetched.
        // this effect flatten the tree of fields.
        if (!getFieldLoading && allFields?.getCctDetailsFields) {
            const flatten = (fields: CctField[]) => {
                const result: typeof fields = [];
                for (const f of fields) {
                    result.push(f);
                    if (f.children?.length) {
                        result.push(...flatten(f.children));
                    }
                }
                return result;
            };

            const _flattenedFields = flatten(allFields.getCctDetailsFields);
            setFlattenedFields(_flattenedFields);
            flattenedValuesRef.current = _flattenedFields.map((f, index) => {
                // we filter KantonRegin values based on mission state code. Then if we have only 1 value left, it will
                // be autoselect by default
                if (
                    ["kantonregion", "kanton", "region", "canton"].includes(
                        f.uniqName.toLowerCase(),
                    )
                ) {
                    const filteredValues = f.values.filter((fideldValueToFilter) => {
                        return (
                            fideldValueToFilter.value
                                ?.toLowerCase()
                                ?.includes(missionStateCode.toLowerCase()) ||
                            fideldValueToFilter.value
                                ?.toLowerCase()
                                ?.includes(missionStateName.toLowerCase())
                        );
                    });
                    if (filteredValues.length > 0) {
                        return filteredValues;
                    } else {
                        return f.values;
                    }
                }

                if (flattenedValuesRef.current[index]?.[0]?.criteriaStructureId === f.id) {
                    return flattenedValuesRef.current[index];
                }
                return f.values;
            });
            setCctDetails({
                // `mission` is Buildable (deeply-partial), so concretize its CCT details to the
                // fragment shape. This cast is for Buildable, not for the schema nullability.
                tempdataCallValues: (mission.currentContract?.cctDetails?.tempdataCallValues ||
                    []) as CctDetailsFieldValuesFieldsFragment[],
                readableValues: (mission.currentContract?.cctDetails?.readableValues ||
                    []) as CctDetailsFieldFullValueFieldsFragment["readableValues"],
            });
            if (_flattenedFields.length === 0) {
                setScreenIsValid(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFields, missionStateCode, missionStateName, getFieldLoading]);

    const fetchChildrenValues = useCallback(
        async (
            field: CctField,
            cctEditionId: string,
            parentValues: CctDetailsFieldValueInput[],
            currentValue?: string | null,
        ) => {
            if (field.children) {
                for (const child of field.children) {
                    const childGlobalIndex = flattenedFields.indexOf(child);
                    if (currentValue === null) {
                        // if current value is an empty value we do not fetch
                        // children value as we know children will be empty value (and APi will crash anyway)
                        flattenedValuesRef.current[childGlobalIndex] = [
                            {
                                value: null,
                                autoSelected: true,
                                criteriaStructureId: child.id,
                                criteriaListEntryId: null,
                            },
                        ];
                        continue;
                    }
                    try {
                        const childValues = await getCctDetailsFieldValues({
                            variables: {
                                cctEditionId: cctEditionId,
                                criteriaStructureId: child.id,
                                parentValues: parentValues,
                            },
                        });
                        if (childValues.error) {
                            setLoadingError(
                                i18n.t(
                                    "screens.user.create_mission_cct_details.error_loading_fields",
                                ),
                            );
                            return;
                        }
                        if (childValues.data) {
                            setLoadingError(undefined);
                            flattenedValuesRef.current[childGlobalIndex] =
                                childValues.data.getCctDetailsFieldValuesFromParent;
                        }
                    } catch (e: unknown) {
                        catchLazyQueryError(e as Error);
                    }
                }
            }
        },
        [flattenedFields, catchLazyQueryError, getCctDetailsFieldValues, i18n],
    );

    const onSelectFieldValue = React.useCallback(
        async (field: CctField, value: CctDetailsFieldValuesFieldsFragment) => {
            if (flattenedFields && mission.currentContract?.cctEditionId) {
                const fieldIndex = flattenedFields.indexOf(field);
                // We use a trick of JSON.stringify and JSON.parse to remove the immutability set by jotai on the
                // mission.currentContract.cctDetails.
                // if we try to use the spread operator the new object will be unmuttable
                const cctDetailsCopy = JSON.parse(
                    JSON.stringify(mission.currentContract.cctDetails),
                ) as CctDetailsFieldFullValueFieldsFragment;
                if (cctDetailsCopy?.tempdataCallValues) {
                    cctDetailsCopy.tempdataCallValues[fieldIndex] = value;
                    cctDetailsCopy.readableValues[fieldIndex] = {
                        question: field.name,
                        answer: value.value || "",
                    };

                    // clean all next fields
                    cctDetailsCopy.tempdataCallValues.length = fieldIndex + 1;

                    // we potentially need to fetch child field values
                    if (field.children?.length) {
                        // fetch children values
                        await fetchChildrenValues(
                            field,
                            mission.currentContract.cctEditionId,
                            cctDetailsCopy.tempdataCallValues,
                            value.value,
                        );
                    }
                    setScreenIsValid(
                        cctDetailsCopy.tempdataCallValues.length === flattenedFields.length,
                    );
                    setCctDetails(cctDetailsCopy);
                }
            }
        },
        [flattenedFields, setCctDetails, setScreenIsValid, mission, fetchChildrenValues],
    );

    React.useEffect(() => {
        // This effect auto select the last field:
        // - only 1 field => autoselect
        // - field can be auto select with existing data
        const nbDetails = mission?.currentContract?.cctDetails?.tempdataCallValues?.length || 0;
        if (!getFieldLoading && nbDetails < flattenedFields.length && flattenedFields.length > 0) {
            const nextField = flattenedFields[nbDetails];
            const nextFieldValues = flattenedValuesRef.current[nbDetails];
            if (nextFieldValues?.length === 1) {
                void onSelectFieldValue(nextField, {
                    value: nextFieldValues[0].value,
                    criteriaStructureId: nextFieldValues[0].criteriaStructureId,
                    criteriaListEntryId: nextFieldValues[0].criteriaListEntryId,
                    autoSelected: true,
                });
            } else if (
                nextFieldValues &&
                AUTO_FILLED_FIELDS["YEAR"].includes(nextField.uniqName.toLowerCase())
            ) {
                // we auto select current year
                const currentYear = new Date().getFullYear();

                if (nextField.type === "enum") {
                    const valueIndex = nextFieldValues.findIndex(
                        (searchElement) => searchElement.value === currentYear.toString(),
                    );
                    if (valueIndex >= 0) {
                        void onSelectFieldValue(nextField, {
                            value: nextFieldValues[valueIndex].value,
                            criteriaStructureId: nextFieldValues[valueIndex].criteriaStructureId,
                            criteriaListEntryId: nextFieldValues[valueIndex].criteriaListEntryId,
                            autoSelected: true,
                        });
                    }
                } else if (nextField.type === "number") {
                    // for number field we just set the current year
                    void onSelectFieldValue(nextField, {
                        value: currentYear.toString(),
                        criteriaStructureId: nextField.id,
                        criteriaListEntryId: currentYear,
                        autoSelected: true,
                    });
                }
            } else if (
                nextFieldValues &&
                AUTO_FILLED_FIELDS["AGE"].includes(nextField.uniqName.toLowerCase())
            ) {
                const age = mission.workerInfo?.birthdate
                    ? getAge(new Date(mission.workerInfo?.birthdate || ""))
                    : null;
                if (age) {
                    // if age is in an enumeration we select it
                    if (nextField.type === "enum") {
                        let valueIndex: number;
                        // if age is bigger than the last entry of ages, we select the last entry.
                        // if last entry is empty, we use 200 to be sure age of worker will be smaller. So in this case
                        // we won't auto select.
                        if (
                            age >
                            parseInt(nextFieldValues[nextFieldValues.length - 1].value || "200")
                        ) {
                            valueIndex = nextFieldValues.length - 1;
                        } else {
                            // we auto select the age if the field is the Age field
                            valueIndex = nextFieldValues.findIndex(
                                (searchElement) => searchElement.value === age.toString(),
                            );
                        }
                        if (valueIndex >= 0) {
                            void onSelectFieldValue(nextField, {
                                value: nextFieldValues[valueIndex].value,
                                criteriaStructureId:
                                    nextFieldValues[valueIndex].criteriaStructureId,
                                criteriaListEntryId:
                                    nextFieldValues[valueIndex].criteriaListEntryId,
                                autoSelected: true,
                            });
                        }
                    } else if (nextField.type === "number") {
                        // for number field we just set the age
                        void onSelectFieldValue(nextField, {
                            value: age.toString(),
                            criteriaListEntryId: age,
                            criteriaStructureId: nextField.id,
                            autoSelected: true,
                        });
                    }
                }
            } else if (
                nextFieldValues &&
                AUTO_FILLED_FIELDS["MISSION_DURATION"].includes(nextField.uniqName.toLowerCase())
            ) {
                let missionDurationMonth;
                if (mission.currentContract?.startDate && mission.currentContract?.endDate) {
                    missionDurationMonth = differenceInMonths(
                        new Date(mission.currentContract.endDate),
                        new Date(mission.currentContract.startDate),
                    );
                } else {
                    missionDurationMonth = 600; // use 50 years for CDI so it is big
                }
                if (missionDurationMonth != null && nextField.type === "number") {
                    // for number field we just set the age
                    void onSelectFieldValue(nextField, {
                        value: missionDurationMonth.toString(),
                        criteriaListEntryId: missionDurationMonth,
                        criteriaStructureId: nextField.id,
                        autoSelected: true,
                    });
                }
            }
        }
    }, [
        mission.currentContract?.cctDetails?.tempdataCallValues,
        mission.currentContract?.endDate,
        mission.currentContract?.startDate,
        flattenedFields,
        mission.workerInfo?.birthdate,
        onSelectFieldValue,
        getFieldLoading,
    ]);

    // If readableValues is defined, all useEffect has finished processing and eventual autofields's values has been pre-filled
    const isReadableValuesReady = !!mission.currentContract?.cctDetails?.readableValues;
    return (
        <VStack gap={1} alignment="stretch">
            {getFieldsError && (
                <ScreenCardHeadband
                    text={i18n.t("screens.user.create_mission_cct_details.error_loading_fields")}
                    mode="error"
                />
            )}
            {loadingError && <ScreenCardHeadband text={loadingError} mode="error" />}
            {getFieldLoading && <Loading />}
            {!getFieldLoading &&
                isReadableValuesReady &&
                hasLength(flattenedFields) &&
                // `flattenedValuesRef` is written from event handlers (CCT field changes)
                // and read here to render the field rows. Refactoring to state would be a
                // larger change; this read is gated on async data being ready so the ref
                // is populated before the JSX evaluates.
                // eslint-disable-next-line react-hooks/refs
                hasLength(flattenedValuesRef.current) &&
                range(
                    Math.min(
                        (mission.currentContract?.cctDetails?.tempdataCallValues?.length || 0) + 1,
                        flattenedFields.length,
                    ),
                    // eslint-disable-next-line react-hooks/refs
                ).map((idx) => {
                    const currentField = flattenedFields[idx];
                    if (
                        flattenedValuesRef.current[idx]?.length === 1 &&
                        flattenedValuesRef.current[idx][0].value === null
                    ) {
                        return null;
                    } else {
                        let currentCctDetailsFieldValue:
                            | SelectItemType<CctDetailsFieldValuesFieldsFragment>
                            | undefined = undefined;
                        if (mission.currentContract?.cctDetails?.tempdataCallValues?.[idx]) {
                            currentCctDetailsFieldValue = {
                                // Concretize the Buildable mission value to the fragment shape.
                                value: mission.currentContract?.cctDetails?.tempdataCallValues[
                                    idx
                                ] as CctDetailsFieldValuesFieldsFragment,
                                label:
                                    mission.currentContract?.cctDetails.tempdataCallValues[idx]
                                        ?.value || "",
                            };
                        }
                        if (currentCctDetailsFieldValue?.value?.autoSelected) {
                            return null;
                        }
                        const bottomComponent = (() => {
                            const BottomComponentWrapper = (p: { children: React.ReactNode }) => (
                                <VStack v="1/3" h="2/3" {...p} />
                            );

                            switch (currentField.uniqName) {
                                case "KantonRegion":
                                case "Kanton":
                                case "Region":
                                    return (
                                        <BottomComponentWrapper>
                                            <BodyMediumText color={colors.greyAccessible}>
                                                {i18n.t(
                                                    "screens.user.create_mission_cct_details.region_help",
                                                )}
                                            </BodyMediumText>
                                        </BottomComponentWrapper>
                                    );
                            }
                        })();

                        const title = (() => {
                            switch (currentField.uniqName) {
                                case "KantonRegion":
                                case "Kanton":
                                case "Region":
                                    return i18n.t(
                                        "screens.user.create_mission_cct_details.region_description",
                                    );
                                case "Mitarbeiterkategorie":
                                case "Catégorie":
                                case "CatégorieProfessionnelle":
                                case "Kategorie":
                                case "Lohnklasse":
                                case "education_level":
                                    return i18n.t(
                                        "screens.user.create_mission_cct_details.personnel_categery",
                                    );
                                case "ExpérienceProfessionnelle":
                                    return i18n.t(
                                        "screens.user.create_mission_cct_details.experience_level",
                                    );
                                case "Einführungszeit":
                                    return i18n.t(
                                        "screens.user.create_mission_cct_details.introduction_period",
                                    );
                                case "Betriebsart":
                                    return i18n.t(
                                        "screens.user.create_mission_cct_details.business_type",
                                    );
                                default:
                                    return currentField.name;
                            }
                        })();
                        return (
                            <>
                                <VStack gap="1/2" key={currentField.id}>
                                    <FullWidthLine color={colors.accentLighter} />
                                    <BodyLargeText>{title}</BodyLargeText>
                                    <></>
                                    {currentField.type === "number" ? (
                                        <QuantityField
                                            label={currentField.name}
                                            value={
                                                currentCctDetailsFieldValue?.value?.value != null &&
                                                currentCctDetailsFieldValue?.value?.value !== ""
                                                    ? parseInt(
                                                          currentCctDetailsFieldValue?.value?.value,
                                                      )
                                                    : undefined
                                            }
                                            onChange={(v) => {
                                                void onSelectFieldValue(currentField, {
                                                    value: v != null ? v.toString() : "",
                                                    criteriaStructureId: currentField.id,
                                                    criteriaListEntryId: v ?? null,
                                                    // Manual change: not auto-selected.
                                                    autoSelected: false,
                                                });
                                            }}
                                        />
                                    ) : (
                                        <SelectField
                                            label={i18n.t(
                                                "screens.user.create_mission_cct_details.default_title",
                                            )}
                                            value={currentCctDetailsFieldValue?.value}
                                            data={
                                                (flattenedValuesRef.current[idx] &&
                                                    flattenedValuesRef.current[idx].map((v) => ({
                                                        value: v,
                                                        label: v.value || "",
                                                    }))) ||
                                                []
                                            }
                                            isDynamicHeight
                                            onSelect={(selection) => {
                                                void onSelectFieldValue(
                                                    currentField,
                                                    selection.value,
                                                );
                                            }}
                                            bottomComponent={bottomComponent}
                                        />
                                    )}
                                </VStack>
                            </>
                        );
                    }
                })}
            <></>
            {!getFieldLoading && missionCct && missionCct.intercomHelpArticleId ? (
                <LinkSmallText
                    onPress={() => {
                        void presentArticleId(missionCct.intercomHelpArticleId as string);
                    }}
                >
                    {i18n.t("screens.user.create_mission_cct_details.show_help")}
                </LinkSmallText>
            ) : null}
        </VStack>
    );
};

export default MissionCctDetails;
